import {agoString} from '@github-ui/ago'
import type {RecentlyTouchedBranch} from '@github-ui/code-view-types'
import type {Repository} from '@github-ui/current-repository'
import {branchPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {useAlive} from '@github-ui/use-alive'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {GitBranchIcon} from '@primer/octicons-react'
import {Flash, LinkButton} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {useCallback, useEffect, useRef, useState} from 'react'

import styles from './RecentlyTouchedBranches.module.css'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'
import {
  useRecentlyTouchedBranches,
  type RecentlyTouchedBranches as RecentlyTouchedBranchesData,
} from '@github-ui/code-view-shared/contexts/RecentlyTouchedBranchesContext'

export function RecentlyTouchedBranches({repo}: {repo: Repository}) {
  const isDataRouter = useIsDataRouterEnabled()

  return isDataRouter ? (
    <RecentlyTouchedBranchesWithContext repo={repo} />
  ) : (
    <RecentlyTouchedBranchesWithFetch repo={repo} />
  )
}

export function RecentlyTouchedBranchesWithContext({repo}: {repo: Repository}) {
  const {branches, channel: aliveChannel, canCreatePullRequest, refetch} = useRecentlyTouchedBranches()

  if (!aliveChannel || !branches) {
    return null
  }

  return (
    <RecentlyTouchedBranchesInner
      branches={branches}
      canCreatePullRequest={canCreatePullRequest}
      channel={aliveChannel}
      fetchRecentlyTouchedBranches={refetch}
      repo={repo}
    />
  )
}

export function RecentlyTouchedBranchesWithFetch({repo}: {repo: Repository}) {
  const [branches, setBranches] = useState<RecentlyTouchedBranch[]>([])
  const [aliveChannel, setAliveChannel] = useState<string | null>()
  const [canCreatePullRequest, setCanCreatePullRequest] = useState<boolean>(true)

  const fetchRecentlyTouchedBranches = useCallback(async () => {
    const response = await verifiedFetchJSON(`/${repo.ownerLogin}/${repo.name}/recently-touched-branches`)

    if (response.ok) {
      const json: RecentlyTouchedBranchesData = await response.json()

      if (json.channel) {
        setAliveChannel(json.channel)
      }
      setBranches(json.branches ?? [])
      setCanCreatePullRequest(json.canCreatePullRequest ?? true)
    }
  }, [repo.name, repo.ownerLogin])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchRecentlyTouchedBranches()
  }, [fetchRecentlyTouchedBranches])

  if (!aliveChannel) {
    return null
  }

  return (
    <RecentlyTouchedBranchesInner
      branches={branches}
      canCreatePullRequest={canCreatePullRequest}
      channel={aliveChannel}
      fetchRecentlyTouchedBranches={fetchRecentlyTouchedBranches}
      repo={repo}
    />
  )
}

export function RecentlyTouchedBranchesInner({
  branches,
  canCreatePullRequest = true,
  channel,
  fetchRecentlyTouchedBranches,
  repo,
}: {
  branches: RecentlyTouchedBranch[]
  canCreatePullRequest?: boolean
  channel: string
  fetchRecentlyTouchedBranches: () => void
  repo: Repository
}) {
  const timeout = useRef<number | null>(null)

  const handleAliveEvent = useCallback(() => {
    // We get a lot of events for one push, so we debounce them.
    if (timeout.current !== null) {
      window.clearTimeout(timeout.current)
    }

    timeout.current = window.setTimeout(() => {
      fetchRecentlyTouchedBranches()
    }, 500)
  }, [fetchRecentlyTouchedBranches])

  useAlive(channel, handleAliveEvent)

  if (!branches) {
    return null
  }

  return (
    <>
      {branches.map((branch, index) => (
        <Flash
          // eslint-disable-next-line @eslint-react/no-array-index-key
          key={branch.branchName + index}
          variant="warning"
          className={styles.Flash}
        >
          <div className={styles.Box}>
            <Octicon icon={GitBranchIcon} className={styles.Octicon} />
            <Link
              to={branchPath({owner: branch.repoOwner, repo: branch.repoName, branch: branch.branchName})}
              reloadDocument
              className="text-bold"
            >
              {repo.ownerLogin !== branch.repoOwner ? `${branch.repoOwner}:` : ''}
              {branch.branchName}
            </Link>
            {` had recent pushes ${agoString(new Date(branch.date))}`}
          </div>

          <LinkButton href={branch.comparePath} variant="primary">
            {canCreatePullRequest ? 'Compare & pull request' : 'Compare'}
          </LinkButton>
        </Flash>
      ))}
    </>
  )
}
